import {
	clampTimeOfDay,
	type LegacyTimeOfDay,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import { resolveOpeningAdjustmentOpenings } from "../../engines/twoRoomOpeningConfig";
import type { BuildInput } from "../../engines/types";

/**
 * Evening "god rays": cheap fake volumetrics. Each glazed exterior
 * opening spills a soft, additive light cone outward into the dusk
 * air. This module owns the *pure* parts — where the cones sit and how
 * strongly they glow — so they can be unit-tested without a WebGL
 * context. The R3F component in `LightShafts.tsx` consumes these.
 *
 * Placement math intentionally mirrors `interactionOverlays.ts` (the
 * canonical opening→world transform) rather than re-deriving it; the
 * test pins the formula so the two can't silently drift.
 */

/** Wall sill base, world Y. Matches `interactionOverlays.ts`. */
const WALL_BASE_Y = 0.165;
/** Facade inset so the cone apex sits at the opening face, not the wall centre. */
const FACADE_INSET_M = 0.09;

/**
 * Time-of-day window for the shafts. They stay dark through the day,
 * fade in approaching golden hour, and reach full strength in the
 * evening — then hold through night, where the interior glow spilling
 * out reads strongest. Smoothstepped so there is no hard pop-in.
 */
const SHAFT_FADE_IN_START = 0.55;
const SHAFT_FADE_IN_END = 0.85;

export interface LightShaftPlacement {
	/** Source opening id — stable React key + dispose bookkeeping. */
	id: string;
	/** Cone apex, at the opening face (world metres). */
	origin: [number, number, number];
	/** Outward facade normal the cone extends along (unit vector). */
	normal: [number, number, number];
	widthM: number;
	heightM: number;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
	const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
	return t * t * (3 - 2 * t);
}

/**
 * Global 0..1 glow strength for the shafts at a given time of day.
 * Zero during daylight (so the effect never renders before golden
 * hour), ramping to full across the dusk band.
 */
export function getLightShaftIntensity(
	timeOfDay: TimeOfDayValue | LegacyTimeOfDay | number,
): number {
	const t = clampTimeOfDay(normalizeTimeOfDay(timeOfDay));
	if (t <= SHAFT_FADE_IN_START) return 0;
	return smoothstep(SHAFT_FADE_IN_START, SHAFT_FADE_IN_END, t);
}

/**
 * One cone per glazed exterior opening. Solid doors and the interior
 * divider are skipped — only glass spills light, and only outward.
 */
export function getLightShaftPlacements(
	buildInput: BuildInput,
): LightShaftPlacement[] {
	const { dimensions } = buildInput;
	// The openings the engine actually BUILDS, not the draft list: a
	// panorama two-room swaps every right-wall window for the glass wall
	// and moves the entry door to the long side. Reading the draft list
	// spilled a hard-edged cone out of a window that is no longer there,
	// straight through the panorama glass.
	const openings = resolveOpeningAdjustmentOpenings(buildInput);
	const W = dimensions.width;
	const L = dimensions.length;
	const placements: LightShaftPlacement[] = [];

	for (const opening of openings) {
		// Interior partition openings never face the exterior air.
		if (opening.wall === "divider") continue;
		// Windows always glaze; doors only when explicitly glazed.
		const glazed = opening.type === "window" || opening.glazed === true;
		if (!glazed) continue;

		const widthM = opening.widthMm / 1000;
		const heightM = opening.heightMm / 1000;
		const yM = WALL_BASE_Y + opening.yMm / 1000 + heightM / 2;

		if (opening.wall === "front" || opening.wall === "back") {
			const xM = -W / 2 + opening.xMm / 1000 + widthM / 2;
			const isFront = opening.wall === "front";
			placements.push({
				id: opening.id,
				origin: [
					xM,
					yM,
					isFront ? L / 2 - FACADE_INSET_M : -L / 2 + FACADE_INSET_M,
				],
				normal: isFront ? [0, 0, 1] : [0, 0, -1],
				widthM,
				heightM,
			});
			continue;
		}

		if (opening.wall === "left" || opening.wall === "right") {
			const zM = -L / 2 + opening.xMm / 1000 + widthM / 2;
			const isLeft = opening.wall === "left";
			placements.push({
				id: opening.id,
				origin: [
					isLeft ? -W / 2 + FACADE_INSET_M : W / 2 - FACADE_INSET_M,
					yM,
					zM,
				],
				normal: isLeft ? [-1, 0, 0] : [1, 0, 0],
				widthM,
				heightM,
			});
		}
	}

	return placements;
}
